/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * The tenant's phone numbers, and what each one does when it rings.
 *
 * Numbers are never bought here. They are purchased on Twilio and imported,
 * which keeps Pearl out of provisioning, billing and regulatory compliance --
 * a UAE number needs local documentation Twilio already handles. Sync is
 * therefore the primary action on this page, not a maintenance afterthought:
 * until it runs, `telephony_numbers` is empty, and an empty table means
 * outbound calling fails with "No outbound phone number is configured",
 * because that is where the caller ID comes from.
 *
 * Sync is additive. A number released on Twilio is deactivated rather than
 * deleted, so call history stays attached to the number that handled it.
 *
 * The exception is a BYOC number: one another carrier owns (a du mobile behind
 * the carrier's SBC) that reaches Twilio over a BYOC trunk. Twilio cannot list
 * those, so they are added by hand with the trunk's SID, and sync leaves them
 * alone. Their "Twilio routing" is the trunk's voice URL, which is shared by
 * every number on that trunk.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { Add, ArrowBack, Sync, WarningAmber } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";
import { getApiErrorMessage } from "utils/apiErrors";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";
import { distinctFriendlyName } from "telephony/callerIdLabel";

const CARD_BORDER = "1px solid #E6E8F0";

/**
 * What happens to an inbound call on this number.
 *
 * `queue` and `direct_user` each need a target, and the server rejects a save
 * without one -- a number pointed at nothing silently drops every call it
 * receives, which is the worst possible failure for an inbound line.
 */
const INBOUND_FLOWS = [
  { value: "queue", label: "Ring a queue", needs: "queue" },
  { value: "direct_user", label: "Ring one person", needs: "user" },
  { value: "ivr", label: "Play a phone menu", needs: "ivr" },
  { value: "voicemail", label: "Voicemail", needs: null },
  // The SEEP AI assistant answers first; the queue is where it hands callers
  // to a person, and where they go if the assistant is unavailable.
  { value: "seep_ai", label: "SEEP AI assistant, then a queue", needs: "queue" },
];

const DIRECTIONS = [
  { value: "both", label: "Inbound & outbound" },
  { value: "inbound", label: "Inbound only" },
  { value: "outbound", label: "Outbound only" },
];

/**
 * What Twilio will actually do when this number rings.
 *
 * This exists because Pearl's own configuration cannot tell you. Twilio
 * ignores a number's voice URL outright when a SIP trunk is attached, so a
 * number can be pointed at a queue here, look entirely healthy, and deliver
 * every inbound call to somebody's PBX instead. `ok: false` means the rows
 * below are describing intent that cannot fire.
 */
const ROUTING_STATUS = {
  pearl: {
    ok: true,
    label: "Pearl",
    color: "success",
    help: "Twilio sends calls on this number to Pearl.",
  },
  trunk: {
    ok: false,
    label: "SIP trunk",
    color: "error",
    help:
      "Twilio routes this number to your SIP trunk, so calls will never reach Pearl. " +
      "A trunk overrides the voice URL completely.",
  },
  other_app: {
    ok: false,
    label: "TwiML App",
    color: "warning",
    help:
      "This number points at a TwiML App, which holds the outbound URL. Inbound " +
      "callers would be sent into the outbound flow.",
  },
  external_url: {
    ok: false,
    label: "Elsewhere",
    color: "warning",
    help: "This number points at a URL that is not Pearl.",
  },
  unrouted: {
    ok: false,
    label: "Not routed",
    color: "warning",
    help: "Twilio has no voice URL for this number, so inbound calls go nowhere.",
  },
  unknown: {
    ok: false,
    // `known: false` separates "we have looked and it is wrong" from "we have
    // not looked yet". Everything above is the former; only this is the
    // latter, and the difference decides whether we are entitled to tell an
    // admin their number is broken. An un-synced number is not evidence of a
    // fault, and the backend's derive_routing_status is careful about that --
    // the page must be too, or it undoes the care.
    known: false,
    label: "Not checked",
    color: "default",
    help: "Sync from Twilio to see where this number's calls go.",
  },
};

const routingMeta = (status) => ROUTING_STATUS[status] || ROUTING_STATUS.unknown;

const isByoc = (number) => number?.provider_kind === "byoc";

const BYOC_SID = /^BY[0-9a-fA-F]{32}$/;
const EMPTY_BYOC = { e164: "", friendly_name: "", byoc_trunk_sid: "", direction_capability: "both" };

/** Twilio is known to send this number somewhere other than Pearl. */
const isKnownBad = (status) => {
  const meta = routingMeta(status);
  return !meta.ok && meta.known !== false;
};

const flowNeeds = (flow) => INBOUND_FLOWS.find((f) => f.value === flow)?.needs || null;

/** The User schema carries first_name/last_name, not a composed full_name. */
const userLabel = (u) =>
  [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || u.username || `User ${u.id}`;

function NumberRow({ number, queues, users, menus, onSave, onClaim, saving, claiming }) {
  const [draft, setDraft] = useState(number);

  useEffect(() => setDraft(number), [number]);

  const set = (field) => (e) => {
    const value = e.target.value;
    setDraft((d) => {
      const next = { ...d, [field]: value };
      // Switching flow away from a target-bearing one leaves a stale id behind,
      // which the server would happily store and nothing would ever read.
      if (field === "inbound_flow") {
        if (flowNeeds(value) !== "queue") next.inbound_queue_id = null;
        if (flowNeeds(value) !== "user") next.direct_user_id = null;
        if (flowNeeds(value) !== "ivr") next.inbound_ivr_id = null;
      }
      return next;
    });
  };

  const routing = routingMeta(number.routing_status);
  // Configuring where a call goes is pointless while Twilio will not deliver
  // one. Leaving these editable invites an admin to "fix" a number by
  // reassigning the queue, which changes nothing.
  // Outbound is deliberately NOT gated: Twilio only checks that you own the
  // number when it is used as a caller ID, so a trunk-attached number still
  // dials out from Pearl perfectly well. It is only inbound that cannot work.
  //
  // Gated on KNOWN bad, not on "not ok". A number that has never been synced
  // reads as unknown, and locking its inbound settings would leave an admin
  // unable to configure a perfectly good number until they happened to press
  // Sync.
  const unreachable = isKnownBad(number.routing_status);
  const needs = flowNeeds(draft.inbound_flow);
  const missingTarget =
    (needs === "queue" && !draft.inbound_queue_id) ||
    (needs === "user" && !draft.direct_user_id) ||
    (needs === "ivr" && !draft.inbound_ivr_id);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(number),
    [draft, number]
  );

  return (
    <TableRow hover>
      <TableCell>
        <MDTypography variant="button" fontWeight="medium">
          {number.e164}
        </MDTypography>
        {distinctFriendlyName(number) ? (
          <MDTypography variant="caption" color="text" display="block">
            {distinctFriendlyName(number)}
          </MDTypography>
        ) : null}
        {isByoc(number) ? (
          <Tooltip title={`Carried by your own carrier over BYOC trunk ${number.twilio_byoc_trunk_sid}`}>
            <Chip size="small" label="BYOC" variant="outlined" sx={{ mt: 0.5 }} />
          </Tooltip>
        ) : null}
      </TableCell>

      <TableCell sx={{ minWidth: 180 }}>
        <Tooltip title={routing.help}>
          <Chip size="small" label={routing.label} color={routing.color} variant="outlined" />
        </Tooltip>
        {unreachable ? (
          <MDBox mt={0.5}>
            <MDButton
              variant="text"
              color="info"
              size="small"
              sx={{ px: 0, minWidth: 0 }}
              disabled={claiming || (!number.twilio_phone_sid && !isByoc(number))}
              onClick={() => onClaim(number)}
            >
              {claiming ? "Pointing…" : "Point at Pearl"}
            </MDButton>
          </MDBox>
        ) : null}
      </TableCell>

      <TableCell sx={{ minWidth: 170 }}>
        <TextField
          select
          fullWidth
          size="small"
          value={draft.direction_capability || "both"}
          onChange={set("direction_capability")}
        >
          {DIRECTIONS.map((d) => (
            <MenuItem key={d.value} value={d.value}>
              {d.label}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>

      <TableCell sx={{ minWidth: 170 }}>
        <TextField
          select
          fullWidth
          size="small"
          disabled={unreachable}
          value={draft.inbound_flow || "queue"}
          onChange={set("inbound_flow")}
        >
          {INBOUND_FLOWS.map((f) => (
            <MenuItem key={f.value} value={f.value}>
              {f.label}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>

      <TableCell sx={{ minWidth: 200 }}>
        {needs === "queue" ? (
          <TextField
            select
            fullWidth
            size="small"
            disabled={unreachable}
            value={draft.inbound_queue_id || ""}
            onChange={set("inbound_queue_id")}
            error={missingTarget && !unreachable}
            helperText={
              unreachable
                ? " "
                : queues.length
                ? missingTarget
                  ? "Pick a queue, or calls are dropped."
                  : " "
                : "No queues yet — create one under Queues & routing."
            }
          >
            {queues.map((q) => (
              <MenuItem key={q.id} value={q.id}>
                {q.name}
              </MenuItem>
            ))}
          </TextField>
        ) : needs === "ivr" ? (
          <TextField
            select
            fullWidth
            size="small"
            value={draft.inbound_ivr_id || ""}
            onChange={set("inbound_ivr_id")}
            error={missingTarget}
            helperText={
              menus.length
                ? missingTarget
                  ? "Pick a menu, or calls are dropped."
                  : (menus.find((m) => m.id === draft.inbound_ivr_id) || {}).is_active === false
                  ? "That menu is switched off — callers fall through to its fallback."
                  : " "
                : "No phone menus yet — build one under Phone menus."
            }
          >
            {menus.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.name}
                {m.is_active ? "" : " (off)"}
              </MenuItem>
            ))}
          </TextField>
        ) : needs === "user" ? (
          <TextField
            select
            fullWidth
            size="small"
            disabled={unreachable}
            value={draft.direct_user_id || ""}
            onChange={set("direct_user_id")}
            error={missingTarget && !unreachable}
            helperText={
              unreachable ? " " : missingTarget ? "Pick someone, or calls are dropped." : " "
            }
          >
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {userLabel(u)}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <MDTypography variant="caption" color="text">
            —
          </MDTypography>
        )}
      </TableCell>

      <TableCell align="center">
        <Tooltip
          title={
            draft.is_active
              ? "Active. Turn off to stop using this number without losing its call history."
              : "Inactive — released on Twilio, or switched off here."
          }
        >
          <span>
            <Switch
              checked={Boolean(draft.is_active)}
              onChange={(e) => setDraft((d) => ({ ...d, is_active: e.target.checked }))}
            />
          </span>
        </Tooltip>
      </TableCell>

      <TableCell align="right">
        <MDButton
          variant="gradient"
          color="info"
          size="small"
          disabled={!dirty || (missingTarget && !unreachable) || saving}
          onClick={() => onSave(draft)}
        >
          Save
        </MDButton>
      </TableCell>
    </TableRow>
  );
}

function TelephonyNumbersPage() {
  const isV2 = useTheme()?.pearlUi === "v2";
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [claimingId, setClaimingId] = useState(null);
  const [claimTarget, setClaimTarget] = useState(null);
  const [byocOpen, setByocOpen] = useState(false);
  const [byocForm, setByocForm] = useState(EMPTY_BYOC);
  const [addingByoc, setAddingByoc] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const [numbers, setNumbers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [users, setUsers] = useState([]);
  const [menus, setMenus] = useState([]);
  const [settings, setSettings] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const settingsRes = await axiosInstance.get("/telephony/settings");
      setSettings(settingsRes.data);

      // Queues, users and menus only populate dropdowns. Losing them must not
      // stop the numbers themselves from rendering.
      const [numbersRes, queuesRes, usersRes, menusRes] = await Promise.allSettled([
        axiosInstance.get("/telephony/numbers", { params: { include_inactive: true } }),
        axiosInstance.get("/call-center/queues", { params: { with_stats: false } }),
        axiosInstance.get("/users/"),
        axiosInstance.get("/call-center/ivr-menus", { params: { include_inactive: true } }),
      ]);
      setNumbers(
        numbersRes.status === "fulfilled" && Array.isArray(numbersRes.value.data)
          ? numbersRes.value.data
          : []
      );
      setQueues(
        queuesRes.status === "fulfilled" && Array.isArray(queuesRes.value.data)
          ? queuesRes.value.data
          : []
      );
      setUsers(
        usersRes.status === "fulfilled" && Array.isArray(usersRes.value.data)
          ? // Support users cannot take calls, and a deactivated user pointed
            // at by a number would drop every call it receives.
            usersRes.value.data.filter((u) => u.is_active && !u.is_support_user)
          : []
      );
      // Inactive menus are listed on purpose: pointing a number at a menu
      // before switching it on is a normal order to do things in, and the row
      // says so rather than hiding the option.
      setMenus(
        menusRes.status === "fulfilled" && Array.isArray(menusRes.value.data)
          ? menusRes.value.data
          : []
      );
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to load phone numbers."));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    load();
  }, [load]);

  const runSync = async () => {
    setSyncing(true);
    setError("");
    try {
      const res = await axiosInstance.post("/telephony/numbers/sync");
      const { imported = 0, updated = 0, total_on_account: total = 0, numbers: rows } = res.data;
      setNumbers(Array.isArray(rows) ? rows : []);
      setToast(
        total === 0
          ? "Twilio returned no phone numbers on this account."
          : `Synced ${total} number(s) — ${imported} new, ${updated} updated.`
      );
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to sync from Twilio."));
    } finally {
      setSyncing(false);
    }
  };

  const saveNumber = async (draft) => {
    setSavingId(draft.id);
    setError("");
    try {
      const res = await axiosInstance.put(`/telephony/numbers/${draft.id}`, {
        direction_capability: draft.direction_capability,
        inbound_flow: draft.inbound_flow,
        inbound_queue_id: draft.inbound_queue_id || null,
        inbound_ivr_id: draft.inbound_ivr_id || null,
        direct_user_id: draft.direct_user_id || null,
        is_active: draft.is_active,
      });
      setNumbers((list) => list.map((n) => (n.id === res.data.id ? res.data : n)));
      setToast(`${res.data.e164} saved.`);
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to save this number."));
    } finally {
      setSavingId(null);
    }
  };

  const claimNumber = async () => {
    const target = claimTarget;
    if (!target) return;
    setClaimTarget(null);
    setClaimingId(target.id);
    setError("");
    try {
      const res = await axiosInstance.post(`/telephony/numbers/${target.id}/claim`);
      if (isByoc(target)) {
        // The URL lives on the trunk, so every number on it changed. Reload
        // rather than patch one row and leave its siblings looking stale.
        await load();
        setToast(`The BYOC trunk for ${res.data.e164} now sends its calls to Pearl.`);
      } else {
        setNumbers((list) => list.map((n) => (n.id === res.data.id ? res.data : n)));
        setToast(`${res.data.e164} now sends its calls to Pearl.`);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to update this number on Twilio."));
    } finally {
      setClaimingId(null);
    }
  };

  const addByocNumber = async () => {
    setAddingByoc(true);
    setError("");
    try {
      const res = await axiosInstance.post("/telephony/numbers/byoc", {
        e164: byocForm.e164.trim(),
        friendly_name: byocForm.friendly_name.trim() || null,
        byoc_trunk_sid: byocForm.byoc_trunk_sid.trim(),
        direction_capability: byocForm.direction_capability,
      });
      setNumbers((list) => [...list, res.data].sort((a, b) => a.e164.localeCompare(b.e164)));
      setByocOpen(false);
      setByocForm(EMPTY_BYOC);
      setToast(
        isKnownBad(res.data.routing_status)
          ? `${res.data.e164} added. Use “Point at Pearl” so its inbound calls arrive here.`
          : `${res.data.e164} added.`
      );
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to add this number."));
    } finally {
      setAddingByoc(false);
    }
  };

  const byocSidValid = BYOC_SID.test(byocForm.byoc_trunk_sid.trim());
  const claimSiblings = claimTarget && isByoc(claimTarget)
    ? numbers.filter(
        (n) => isByoc(n) && n.twilio_byoc_trunk_sid === claimTarget.twilio_byoc_trunk_sid
      )
    : [];

  const configured = Boolean(settings?.is_configured);
  const unreachable = numbers.filter((n) => n.is_active && isKnownBad(n.routing_status));
  const trunkCount = unreachable.filter((n) => n.routing_status === "trunk").length;
  const unchecked = numbers.filter(
    (n) => n.is_active && routingMeta(n.routing_status).known === false
  );
  const outboundReady = numbers.some(
    (n) => n.is_active && ["outbound", "both"].includes(n.direction_capability)
  );

  // The header's actions, shared by the v2 page header and the classic one.
  const headerActions = (
    <>
      <MDButton
        variant="outlined"
        color="info"
        onClick={() => setByocOpen(true)}
        disabled={!configured}
        startIcon={<Add />}
      >
        Add BYOC number
      </MDButton>
      <MDButton
        variant="gradient"
        color="info"
        onClick={runSync}
        disabled={syncing || !configured}
        startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <Sync />}
      >
        {syncing ? "Syncing…" : "Sync from Twilio"}
      </MDButton>
    </>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={isV2 ? 0 : 3}>
        {isV2 ? (
          <PageHeader
            title="Phone numbers"
            summary="Numbers are bought on Twilio and imported here, or added by hand when your own carrier reaches Twilio over a BYOC trunk. Choose what each one does when it rings."
            actions={headerActions}
          />
        ) : (
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <IconButton onClick={() => navigate("/settings/telephony")}>
              <ArrowBack />
            </IconButton>
            <MDBox flexGrow={1}>
              <MDTypography variant="h5" fontWeight="medium">
                Phone numbers
              </MDTypography>
              <MDTypography variant="body2" color="text">
                Numbers are bought on Twilio and imported here, or added by hand when your own
                carrier reaches Twilio over a BYOC trunk. Choose what each one does when it rings.
              </MDTypography>
            </MDBox>
            {headerActions}
          </MDBox>
        )}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        {!loading && !configured ? (
          <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }}>
            Connect your Twilio account before importing numbers — Pearl needs it to read the
            numbers you own.
          </Alert>
        ) : null}

        {!loading && configured && numbers.length > 0 && !outboundReady ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No active number can place outbound calls, so the softphone will refuse to dial. Set at
            least one to “Inbound &amp; outbound”.
          </Alert>
        ) : null}

        {!loading && configured && unreachable.length === 0 && unchecked.length > 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Pearl has not checked where{" "}
            {unchecked.length === 1 ? "this number's" : "these numbers'"} calls go on
            Twilio yet. Sync to find out.
          </Alert>
        ) : null}

        {!loading && unreachable.length > 0 ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {unreachable.length} active number
            {unreachable.length === 1 ? "" : "s"} will never receive a call in Pearl, because Twilio
            is set to send{unreachable.length === 1 ? "" : " them"} somewhere else.
            {trunkCount > 0
              ? " A number attached to a SIP trunk has its voice URL ignored entirely, so no amount of configuration here will make it ring."
              : ""}{" "}
            Use “Point at Pearl” on each one.
          </Alert>
        ) : null}

        {loading ? (
          <MDBox display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </MDBox>
        ) : (
          <Grid container>
            <Grid item xs={12}>
              <Card sx={{ p: 3, border: CARD_BORDER, boxShadow: "none", borderRadius: "12px" }}>
                {numbers.length === 0 ? (
                  <MDBox textAlign="center" py={6}>
                    <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
                      No numbers imported yet
                    </MDTypography>
                    <MDTypography variant="body2" color="text" mb={3}>
                      {configured
                        ? "Sync to pull in the numbers on your Twilio account. Nothing is purchased or changed on Twilio."
                        : "Connect your Twilio account first."}
                    </MDTypography>
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={runSync}
                      disabled={syncing || !configured}
                    >
                      {syncing ? "Syncing…" : "Sync from Twilio"}
                    </MDButton>
                  </MDBox>
                ) : (
                  <MDBox sx={{ overflowX: "auto" }}>
                    <Table>
                      <TableHead sx={{ display: "table-header-group" }}>
                        <TableRow>
                          <TableCell>Number</TableCell>
                          <TableCell>Twilio routing</TableCell>
                          <TableCell>Direction</TableCell>
                          <TableCell>When it rings</TableCell>
                          <TableCell>Goes to</TableCell>
                          <TableCell align="center">Active</TableCell>
                          <TableCell align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {numbers.map((n) => (
                          <NumberRow
                            key={n.id}
                            number={n}
                            queues={queues}
                            users={users}
                            menus={menus}
                            onSave={saveNumber}
                            onClaim={setClaimTarget}
                            saving={savingId === n.id}
                            claiming={claimingId === n.id}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </MDBox>
                )}
              </Card>
            </Grid>
          </Grid>
        )}

        {numbers.length > 0 ? (
          <MDBox mt={2} display="flex" alignItems="center" gap={1}>
            <Chip size="small" label={`${numbers.filter((n) => n.is_active).length} active`} />
            <MDTypography variant="caption" color="text">
              A number released on Twilio is switched off here rather than deleted, so its call
              history stays attached.
            </MDTypography>
          </MDBox>
        ) : null}
      </MDBox>

      {/*
        A confirm step rather than a one-click action, because this writes to
        the tenant's Twilio account. For a trunk number it also takes the
        number away from their PBX, which is not something to discover after
        the fact.
      */}
      <Dialog open={Boolean(claimTarget)} onClose={() => setClaimTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Point {claimTarget?.e164} at Pearl?</DialogTitle>
        <DialogContent>
          {isByoc(claimTarget) ? (
            <>
              <MDTypography variant="body2" color="text" mb={2}>
                This number is reached over BYOC trunk{" "}
                <MDTypography variant="caption" fontWeight="medium" component="span">
                  {claimTarget?.twilio_byoc_trunk_sid}
                </MDTypography>
                . Pearl will set the trunk&apos;s voice webhook on Twilio, so incoming calls from
                your carrier arrive in the call center.
              </MDTypography>
              <Alert severity="info" sx={{ mb: 2 }}>
                The webhook belongs to the trunk, not the number.{" "}
                <strong>Every call your carrier sends over this trunk will come to Pearl</strong>
                {claimSiblings.length > 1
                  ? `, including the ${claimSiblings.length - 1} other number(s) on it here`
                  : ""}
                .
              </Alert>
            </>
          ) : (
            <MDTypography variant="body2" color="text" mb={2}>
              Pearl will set this number&apos;s voice webhook on Twilio so its incoming calls arrive
              in the call center.
            </MDTypography>
          )}

          {claimTarget?.routing_status === "trunk" ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This number is attached to a SIP trunk. Pearl has to remove it from the trunk first,
              because Twilio ignores the voice webhook while a trunk is attached.{" "}
              <strong>Your PBX will stop receiving calls on this number.</strong> You can re-attach
              it in the Twilio console at any time.
            </Alert>
          ) : null}

          {claimTarget?.twilio_voice_url && claimTarget?.routing_status === "external_url" ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {isByoc(claimTarget) ? "The trunk" : "This number"} currently points at{" "}
              <MDTypography variant="caption" fontWeight="medium" component="span">
                {claimTarget.twilio_voice_url}
              </MDTypography>
              , which will be replaced.
            </Alert>
          ) : null}

          <MDTypography variant="caption" color="text">
            {isByoc(claimTarget)
              ? "Nothing is purchased, and your carrier and SBC settings are not touched."
              : "Nothing is purchased and no other number is affected."}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="dark" onClick={() => setClaimTarget(null)}>
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={claimNumber}>
            Point at Pearl
          </MDButton>
        </DialogActions>
      </Dialog>

      {/*
        BYOC numbers belong to another carrier, so Twilio cannot list them and
        sync will never find them. The trunk SID is checked against Twilio
        before anything is saved.
      */}
      <Dialog open={byocOpen} onClose={() => setByocOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add a BYOC number</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text" mb={2}>
            For a number your own carrier owns (for example a du number behind your SBC) that
            reaches Twilio over a BYOC trunk. Outbound calls from it leave through that trunk.
          </MDTypography>
          <TextField
            fullWidth
            margin="dense"
            label="Phone number"
            placeholder="+971501234567"
            value={byocForm.e164}
            onChange={(e) => setByocForm((f) => ({ ...f, e164: e.target.value }))}
            helperText="International format is safest. A local number is read in your default region."
          />
          <TextField
            fullWidth
            margin="dense"
            label="Label (optional)"
            value={byocForm.friendly_name}
            onChange={(e) => setByocForm((f) => ({ ...f, friendly_name: e.target.value }))}
          />
          <TextField
            fullWidth
            margin="dense"
            label="BYOC trunk SID"
            placeholder="BYxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            value={byocForm.byoc_trunk_sid}
            onChange={(e) => setByocForm((f) => ({ ...f, byoc_trunk_sid: e.target.value }))}
            error={Boolean(byocForm.byoc_trunk_sid) && !byocSidValid}
            helperText="Twilio console › Voice › BYOC Trunks. Starts with BY."
          />
          <TextField
            select
            fullWidth
            margin="dense"
            label="Direction"
            value={byocForm.direction_capability}
            onChange={(e) => setByocForm((f) => ({ ...f, direction_capability: e.target.value }))}
          >
            {DIRECTIONS.map((d) => (
              <MenuItem key={d.value} value={d.value}>
                {d.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="dark" onClick={() => setByocOpen(false)}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={addByocNumber}
            disabled={addingByoc || !byocForm.e164.trim() || !byocSidValid}
          >
            {addingByoc ? "Adding…" : "Add number"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </DashboardLayout>
  );
}

export default TelephonyNumbersPage;
